import React, { useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { ArrowLeft, Link2, Download, CheckCircle, XCircle, Loader2, Search, Upload, FileSpreadsheet } from 'lucide-react';

const PropertyImport = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [singleUrl, setSingleUrl] = useState('');
  const [bulkUrls, setBulkUrls] = useState('');
  const [listingUrl, setListingUrl] = useState('');
  const [maxProperties, setMaxProperties] = useState(50);
  const [loading, setLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [lineVoomUrls, setLineVoomUrls] = useState('');

  const handleCSVImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setLoading(true);
    setImportResults(null);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await axios.post(`${API}/properties/import-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`Imported ${response.data.imported_count} properties!`);
      setImportResults({
        type: 'csv',
        ...response.data
      });
      setCsvFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'CSV import failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSingleImport = async () => {
    if (!singleUrl.trim()) {
      toast.error('Please enter a property URL');
      return;
    }

    setLoading(true);
    setImportResults(null);
    try {
      const response = await axios.post(`${API}/properties/import-url`, {
        url: singleUrl.trim()
      });
      
      toast.success('Property imported successfully!');
      setImportResults({
        type: 'single',
        success: true,
        property: response.data
      });
      setSingleUrl('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setImportResults({
        type: 'single',
        success: false,
        error: error.response?.data?.detail || 'Import failed'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkImport = async () => {
    const urls = bulkUrls.split('\n').map(u => u.trim()).filter(u => u);
    
    if (urls.length === 0) {
      toast.error('Please enter at least one URL');
      return;
    }

    setLoading(true);
    setImportResults(null);
    try {
      const response = await axios.post(`${API}/properties/import-bulk`, {
        urls: urls
      });
      
      toast.success(`Imported ${response.data.success.length} of ${response.data.total} properties`);
      setImportResults({
        type: 'bulk',
        ...response.data
      });
      setBulkUrls('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk import failed');
    } finally {
      setLoading(false);
    }
  };

  const handleListingCrawl = async () => {
    if (!listingUrl.trim()) {
      toast.error('Please enter a listing page URL');
      return;
    }

    setLoading(true);
    setImportResults(null);
    try {
      const response = await axios.post(`${API}/properties/import-listing-page`, {
        listing_url: listingUrl.trim(),
        max_properties: maxProperties
      });
      
      if (response.data.total === 0) {
        toast.error('No properties found on this page');
        setImportResults({
          type: 'crawler',
          success: false,
          message: response.data.message || 'No property URLs found on this listing page'
        });
      } else {
        toast.success(`Found ${response.data.total} properties, imported ${response.data.success?.length || 0}`);
        setImportResults({
          type: 'crawler',
          ...response.data
        });
        setListingUrl('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Listing import failed');
      setImportResults({
        type: 'crawler',
        success: false,
        error: error.response?.data?.detail || 'Listing import failed'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLineVoomImport = async () => {
    const urls = lineVoomUrls.split('\n').map(u => u.trim()).filter(u => u);
    
    if (urls.length === 0) {
      toast.error('Please enter at least one Line Voom URL');
      return;
    }

    // Validate Line Voom URLs
    const invalidUrls = urls.filter(u => !u.includes('linevoom.line.me'));
    if (invalidUrls.length > 0) {
      toast.error('All URLs must be from linevoom.line.me');
      return;
    }

    setLoading(true);
    setImportResults(null);
    try {
      const response = await axios.post(`${API}/properties/import-line-voom`, {
        urls: urls
      });
      
      toast.success(`Imported ${response.data.success.length} of ${response.data.total} properties from Line Voom`);
      setImportResults({
        type: 'linevoom',
        ...response.data
      });
      setLineVoomUrls('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Line Voom import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      {/* Header */}
      <header className="crystal-glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                 
                 
                className="hidden"
                onClick={() => navigate('/')}
              />
              <div>
                <h1 className="font-heading text-2xl font-semibold text-[#D4AF37]">Property Import</h1>
                <p className="text-sm text-[#94A3B8]">Import from real estate websites</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/admin/dashboard')} 
              variant="outline"
              className="border-white/10 text-[#94A3B8] hover:bg-white/5 hover:text-white rounded-xl"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* VERIFIED WORKING URLs */}
        <Card className="mb-8 border-emerald-500/20 bg-emerald-500/5">
          <CardHeader>
            <CardTitle className="font-heading text-2xl text-emerald-400">✅ VERIFIED Working URLs - Copy & Use!</CardTitle>
            <CardDescription className="text-emerald-400 font-semibold">
              All URLs tested and confirmed working - just copy and paste into Listing Crawler below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Pattaya */}
              <div className="bg-[#0A0E27] rounded-xl p-4 border border-emerald-500/20">
                <h3 className="font-bold text-[#D4AF37] mb-3 flex items-center gap-2">
                  📍 PATTAYA (1,000+ Properties)
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-[#94A3B8] min-w-[80px]">All Sale:</span>
                    <code className="flex-1 bg-white/5 px-2 py-1 rounded text-xs">
                      https://www.thailand-property.com/properties-for-sale/chonburi/pattaya
                    </code>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-[#94A3B8] min-w-[80px]">All Rent:</span>
                    <code className="flex-1 bg-white/5 px-2 py-1 rounded text-xs">
                      https://www.thailand-property.com/properties-for-rent/chonburi/pattaya
                    </code>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-[#94A3B8] min-w-[80px]">Condos:</span>
                    <code className="flex-1 bg-white/5 px-2 py-1 rounded text-xs">
                      https://www.thailand-property.com/condos-for-rent/chonburi/pattaya
                    </code>
                  </div>
                </div>
              </div>

              {/* Jomtien */}
              <div className="bg-[#0A0E27] rounded-xl p-4 border border-emerald-500/20">
                <h3 className="font-bold text-[#D4AF37] mb-3 flex items-center gap-2">
                  🏖️ JOMTIEN BEACH (500+ Properties)
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-[#94A3B8] min-w-[80px]">All Sale:</span>
                    <code className="flex-1 bg-white/5 px-2 py-1 rounded text-xs">
                      https://www.thailand-property.com/properties-for-sale/chonburi/pattaya/jomtien
                    </code>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-[#94A3B8] min-w-[80px]">All Rent:</span>
                    <code className="flex-1 bg-white/5 px-2 py-1 rounded text-xs">
                      https://www.thailand-property.com/properties-for-rent/chonburi/pattaya/jomtien
                    </code>
                  </div>
                </div>
              </div>

              {/* Sattahip */}
              <div className="bg-[#0A0E27] rounded-xl p-4 border border-emerald-500/20">
                <h3 className="font-bold text-[#D4AF37] mb-3 flex items-center gap-2">
                  ⚓ SATTAHIP (2,600+ Properties)
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-[#94A3B8] min-w-[80px]">All Sale:</span>
                    <code className="flex-1 bg-white/5 px-2 py-1 rounded text-xs">
                      https://www.thailand-property.com/properties-for-sale/chonburi/sattahip
                    </code>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-[#94A3B8] min-w-[80px]">All Rent:</span>
                    <code className="flex-1 bg-white/5 px-2 py-1 rounded text-xs">
                      https://www.thailand-property.com/properties-for-rent/chonburi/sattahip
                    </code>
                  </div>
                </div>
              </div>

              {/* Rayong */}
              <div className="bg-[#0A0E27] rounded-xl p-4 border border-emerald-500/20">
                <h3 className="font-bold text-[#D4AF37] mb-3 flex items-center gap-2">
                  🌴 RAYONG (900+ Properties)
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-[#94A3B8] min-w-[80px]">All Sale:</span>
                    <code className="flex-1 bg-white/5 px-2 py-1 rounded text-xs">
                      https://www.thailand-property.com/properties-for-sale/rayong
                    </code>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-[#94A3B8] min-w-[80px]">All Rent:</span>
                    <code className="flex-1 bg-white/5 px-2 py-1 rounded text-xs">
                      https://www.thailand-property.com/properties-for-rent/rayong
                    </code>
                  </div>
                </div>
              </div>

              <Alert className="bg-blue-500/5 border-blue-500/20">
                <AlertDescription className="text-blue-300 text-sm">
                  <strong>💡 How to use:</strong> Copy any URL above, paste it in the Listing Crawler below, and click Import!
                  <br/>
                  <strong>📊 Total Available:</strong> 5,000+ properties across all areas
                </AlertDescription>
              </Alert>
            </div>
          </CardContent>
        </Card>

        {/* Supported Sites */}
        <Card className="mb-8 border-white/10">
          <CardHeader>
            <CardTitle className="font-display text-2xl text-[#D4AF37]">Site Compatibility Status</CardTitle>
            <CardDescription>Which Thai real estate sites work with our importer</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 bg-green-50 rounded-lg border-2 border-emerald-500/20">
                <p className="font-semibold text-emerald-300 flex items-center gap-2">
                  ✅ Thailand Property
                </p>
                <p className="text-xs text-emerald-400">thailand-property.com - WORKS PERFECTLY</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg border border-red-500/20 opacity-60">
                <p className="font-semibold text-red-300 flex items-center gap-2">
                  ❌ Fazwaz
                </p>
                <p className="text-xs text-red-400">fazwaz.com - Blocked</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg border border-red-500/20 opacity-60">
                <p className="font-semibold text-red-300 flex items-center gap-2">
                  ❌ Hipflat
                </p>
                <p className="text-xs text-red-400">hipflat.com - Blocked</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg border border-red-500/20 opacity-60">
                <p className="font-semibold text-red-300 flex items-center gap-2">
                  ❌ DDproperty
                </p>
                <p className="text-xs text-red-400">ddproperty.com - Blocked</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg border border-red-500/20 opacity-60">
                <p className="font-semibold text-red-300 flex items-center gap-2">
                  ❌ BaanFinder
                </p>
                <p className="text-xs text-red-400">baanfinder.com - Limited</p>
              </div>
              <div className="p-3 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
                <p className="font-semibold text-yellow-300 flex items-center gap-2">
                  ⚠️ Others
                </p>
                <p className="text-xs text-yellow-300">Most sites require thailand-property.com</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CSV IMPORT - NEW EASIEST METHOD! */}
        <Card className="mb-8 border-blue-500/20 bg-blue-500/5">
          <CardHeader>
            <CardTitle className="font-heading text-2xl text-blue-400 flex items-center gap-2">
              <FileSpreadsheet className="w-6 h-6" />
              🎉 CSV Import - Upload Pre-Scraped Properties!
            </CardTitle>
            <CardDescription className="text-blue-400 font-semibold">
              I've already scraped 15 Pattaya properties for you - just download and upload!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-emerald-500/10 border-emerald-500/20">
              <AlertDescription className="text-emerald-300">
                <strong>✨ READY TO USE!</strong> I've created a CSV file with 15 real Pattaya properties (with images, prices, details).
                <br/><strong>Just click "Download Sample" below to get it!</strong>
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <div>
                <Label className="text-blue-300 font-semibold">Step 1: Download Pre-Scraped CSV</Label>
                <div className="mt-2">
                  <a 
                    href="/thailand_properties_import.csv" 
                    download="thailand_properties.csv"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Download className="w-4 h-4" />
                    Download 15 Pattaya Properties (CSV)
                  </a>
                  <p className="text-xs text-[#94A3B8] mt-2">
                    Contains: 15 Pattaya condos with real data, images, and prices
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="csv-file" className="text-blue-300 font-semibold">Step 2: Upload CSV File</Label>
                <Input
                  id="csv-file"
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files[0])}
                  className="border-blue-500/20"
                  data-testid="csv-file-input"
                />
                {csvFile && (
                  <p className="text-sm text-emerald-500 mt-1">
                    ✓ Selected: {csvFile.name}
                  </p>
                )}
              </div>

              <Button 
                onClick={handleCSVImport}
                disabled={loading || !csvFile}
                className="bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl"
                data-testid="import-csv-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing CSV...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Properties from CSV
                  </>
                )}
              </Button>
            </div>

            <Alert className="bg-blue-500/5 border-blue-500/20">
              <AlertDescription className="text-blue-300 text-sm">
                <strong>💡 CSV Format:</strong> The CSV file has columns for title, description, price, bedrooms, bathrooms, images, etc.
                <br/>You can also create your own CSV file with the same format!
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* LINE VOOM IMPORT - NEW! */}
        <Card className="mb-8 border-purple-500/20 bg-purple-500/5">
          <CardHeader>
            <CardTitle className="font-heading text-2xl text-purple-400 flex items-center gap-2">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.768.039 1.085l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
              </svg>
              🎉 LINE VOOM Import
            </CardTitle>
            <CardDescription className="text-purple-400 font-semibold">
              Import properties directly from your LINE VOOM posts!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-purple-500/10 border-purple-500/20">
              <AlertDescription className="text-purple-300">
                <strong>✨ Perfect for LINE users!</strong> Paste your LINE VOOM post URLs (one per line) and we'll automatically extract property details, price, and location.
                <div className="mt-2 space-y-1 text-xs">
                  <div><strong>✅ What we extract:</strong> Title, Description, Price, Bedrooms, Bathrooms, Size, Contact Info</div>
                  <div><strong>📸 Images:</strong> 1 preview image is imported automatically</div>
                  <div className="text-purple-300 font-semibold mt-2">
                    💡 <strong>To add more photos:</strong> After import, click the purple 📸 button on the property card in your dashboard to quickly add all remaining images!
                  </div>
                </div>
              </AlertDescription>
            </Alert>

            <div className="bg-blue-500/5 p-4 rounded-xl border border-blue-500/20">
              <h4 className="font-semibold text-blue-300 mb-2">📖 Complete Workflow:</h4>
              <ol className="text-sm text-blue-300 space-y-1 list-decimal list-inside">
                <li>Paste your Line Voom post URLs below and click "Import"</li>
                <li>Property details will be imported with 1 preview image</li>
                <li>Go to Admin Dashboard → Find your imported property</li>
                <li>Click the purple <strong>📸 image button</strong> on the property card</li>
                <li>Right-click images in your Line Voom post → "Copy image address"</li>
                <li>Paste all image URLs in the Quick Edit dialog</li>
                <li>Done! Property now has all photos ✨</li>
              </ol>
            </div>

            <div>
              <Label htmlFor="linevoom-urls" className="text-purple-300 font-semibold">LINE VOOM Post URLs (one per line)</Label>
              <Textarea
                id="linevoom-urls"
                value={lineVoomUrls}
                onChange={(e) => setLineVoomUrls(e.target.value)}
                placeholder="https://linevoom.line.me/post/1176154680811289227
https://linevoom.line.me/post/1176987654321098765
https://linevoom.line.me/post/1175123456789012345"
                rows={5}
                className="border-purple-500/20 focus:ring-[#D4AF37]/20 font-mono text-sm"
              />
              <p className="text-xs text-purple-400 mt-2">
                <strong>How to get LINE VOOM URLs:</strong><br/>
                1. Open your LINE VOOM post<br/>
                2. Click "Share" → "Copy Link"<br/>
                3. Paste here (one URL per line for multiple posts)
              </p>
            </div>
            
            <Button 
              onClick={handleLineVoomImport}
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-500 text-white font-semibold rounded-xl text-lg py-6 w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Importing from LINE VOOM...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 mr-2" />
                  🚀 Import from LINE VOOM
                </>
              )}
            </Button>

            <Alert className="bg-purple-500/5 border-purple-500/20">
              <AlertDescription className="text-purple-300 text-sm">
                <strong>💡 Why only 1 image?</strong> Line Voom loads additional images with JavaScript which scrapers can't access. But don't worry! After import, use the <strong className="text-purple-300">Quick Edit Images (📸)</strong> button in your dashboard to add all remaining photos in seconds!
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* LISTING CRAWLER - NEW FEATURE */}
        <Card className="mb-8 border-emerald-500/20 bg-emerald-500/5">
          <CardHeader>
            <CardTitle className="font-heading text-2xl text-emerald-400 flex items-center gap-2">
              <Search className="w-6 h-6" />
              🔥 Listing Page Crawler (Easiest Way!)
            </CardTitle>
            <CardDescription className="text-emerald-400 font-semibold">
              Paste ANY listing page URL and import ALL properties automatically!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-emerald-500/10 border-emerald-500/20">
              <AlertDescription className="text-emerald-300">
                <strong>✨ Super Easy!</strong> Paste a LISTING page showing multiple properties (not a single property page).
                <div className="mt-2 space-y-1 text-xs">
                  <div><strong>✅ RIGHT:</strong> hipflat.com/condo-for-rent/pattaya (shows 20+ properties)</div>
                  <div><strong>❌ WRONG:</strong> hipflat.com/projects/condo-name-123 (single property)</div>
                </div>
              </AlertDescription>
            </Alert>

            <div>
              <Label htmlFor="listing-url" className="text-emerald-300 font-semibold">Listing Page URL (Search Results Page)</Label>
              <Input
                id="listing-url"
                value={listingUrl}
                onChange={(e) => setListingUrl(e.target.value)}
                placeholder="https://www.hipflat.com/condo-for-rent/pattaya"
                className="border-emerald-500/20 focus:ring-[#D4AF37]/20"
                data-testid="listing-url-input"
              />
              <p className="text-xs text-emerald-400 mt-2">
                <strong>Examples of LISTING PAGES (showing multiple properties):</strong><br/>
                • https://www.hipflat.com/condo-for-rent/pattaya<br/>
                • https://www.thailand-property.com/condos-for-rent/chonburi/pattaya<br/>
                • https://www.fazwaz.com/condo-for-sale/thailand/bangkok<br/>
                <br/>
                <strong className="text-red-400">Don't use single property URLs here!</strong> Use "Import Single Property" section below for those.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="max-properties">Max Properties to Import</Label>
                <Input
                  id="max-properties"
                  type="number"
                  min="1"
                  max="100"
                  value={maxProperties}
                  onChange={(e) => setMaxProperties(parseInt(e.target.value))}
                  className="border-emerald-500/20"
                />
              </div>
            </div>
            
            <Button 
              onClick={handleListingCrawl}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl text-lg py-6"
              data-testid="crawl-listing-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Finding & Importing Properties...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5 mr-2" />
                  🚀 Find & Import All Properties
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Single Import */}
        <Card className="mb-8 border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-[#D4AF37]">Import Single Property</CardTitle>
            <CardDescription>For importing ONE specific property - paste the property's detail page URL</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-amber-50 border-white/10">
              <AlertDescription className="text-[#D4AF37]">
                <strong>For Single Properties Only:</strong> Use this when you have a URL to ONE specific property.
                <div className="mt-2 space-y-1 text-xs">
                  <div><strong>✅ Use this for:</strong> hipflat.com/property/condo-name-123</div>
                  <div><strong>❌ Don't use for:</strong> hipflat.com/condo-for-rent/pattaya (use Crawler above)</div>
                </div>
              </AlertDescription>
            </Alert>

            <div>
              <Label htmlFor="single-url">Single Property URL</Label>
              <Input
                id="single-url"
                value={singleUrl}
                onChange={(e) => setSingleUrl(e.target.value)}
                placeholder="https://www.fazwaz.com/property/condo-view-talay-123456"
                className="border-white/10 focus:ring-[#D4AF37]/20"
                data-testid="single-url-input"
              />
              <p className="text-xs text-[#475569] mt-1">Example: https://www.thailand-property.com/property/12345/luxury-condo</p>
            </div>
            
            <Button 
              onClick={handleSingleImport}
              disabled={loading}
              className="bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-xl"
              data-testid="import-single-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  Import Property
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Bulk Import */}
        <Card className="mb-8 border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-[#D4AF37]">Bulk Import Properties</CardTitle>
            <CardDescription>Import multiple properties at once (one URL per line)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="bulk-urls">Property URLs (one per line)</Label>
              <Textarea
                id="bulk-urls"
                value={bulkUrls}
                onChange={(e) => setBulkUrls(e.target.value)}
                placeholder="https://www.fazwaz.com/property/...
https://www.hipflat.com/property/...
https://www.thailand-property.com/property/..."
                rows={8}
                className="border-white/10 focus:ring-[#D4AF37]/20 font-mono text-sm"
                data-testid="bulk-urls-input"
              />
              <p className="text-xs text-[#475569] mt-1">Paste multiple URLs, each on a new line</p>
            </div>
            
            <Button 
              onClick={handleBulkImport}
              disabled={loading}
              className="bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-xl"
              data-testid="import-bulk-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Import All Properties
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        {importResults && (
          <Card className={`border border-red-500/20'}`}>
            <CardHeader>
              <CardTitle className="font-heading text-xl text-[#D4AF37] flex items-center gap-2">
                {importResults.success || (importResults.type === 'bulk' && importResults.success?.length > 0) ? (
                  <CheckCircle className="text-emerald-500" />
                ) : (
                  <XCircle className="text-red-400" />
                )}
                Import Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              {importResults.type === 'single' && importResults.success && (
                <div className="space-y-2">
                  <Alert className="bg-emerald-500/5 border-emerald-500/20">
                    <AlertDescription className="text-emerald-300">
                      <strong>Success!</strong> Property "{importResults.property.title}" imported successfully.
                      <div className="mt-2">
                        <Button 
                          size="sm" 
                          onClick={() => navigate(`/property/${importResults.property.id}`)}
                          className="bg-emerald-600 hover:bg-emerald-500"
                        >
                          View Property
                        </Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {importResults.type === 'single' && !importResults.success && (
                <Alert className="bg-red-500/5 border-red-500/20">
                  <AlertDescription className="text-red-300">
                    <strong>Error:</strong> {importResults.error}
                  </AlertDescription>
                </Alert>
              )}

              {importResults.type === 'bulk' && (
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="p-4 bg-emerald-500/10 rounded-xl flex-1">
                      <p className="text-2xl font-bold text-emerald-400">{importResults.success?.length || 0}</p>
                      <p className="text-sm text-emerald-500">Imported Successfully</p>
                    </div>
                    <div className="p-4 bg-red-500/10 rounded-xl flex-1">
                      <p className="text-2xl font-bold text-red-400">{importResults.failed?.length || 0}</p>
                      <p className="text-sm text-red-400">Failed to Import</p>
                    </div>
                  </div>

                  {importResults.failed && importResults.failed.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-red-300 mb-2">Failed URLs:</h4>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {importResults.failed.map((item, idx) => (
                          <div key={idx} className="text-xs text-red-400 bg-red-50 p-2 rounded">
                            <p className="font-mono truncate">{item.url}</p>
                            <p className="text-red-400">{item.error}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button onClick={() => navigate('/admin/dashboard')}>
                      Go to Dashboard
                    </Button>
                    <Button variant="outline" onClick={() => setImportResults(null)}>
                      Import More
                    </Button>
                  </div>
                </div>
              )}

              {importResults.type === 'crawler' && (
                <div className="space-y-4">
                  <Alert className={importResults.found_urls > 0 ? "bg-emerald-500/5 border-emerald-500/20" : "bg-red-500/5 border-red-500/20"}>
                    <AlertDescription className={importResults.found_urls > 0 ? "text-emerald-300" : "text-red-300"}>
                      {importResults.found_urls > 0 ? (
                        <>
                          <strong>🎉 Listing Crawl Complete!</strong><br/>
                          Found <strong>{importResults.found_urls}</strong> property URLs on the listing page.
                        </>
                      ) : (
                        <>
                          <strong>No Properties Found</strong><br/>
                          {importResults.message || 'Could not find property links on this page.'}
                        </>
                      )}
                    </AlertDescription>
                  </Alert>

                  {importResults.found_urls > 0 && (
                    <>
                      <div className="flex gap-4">
                        <div className="p-4 bg-blue-500/10 rounded-xl flex-1">
                          <p className="text-2xl font-bold text-blue-400">{importResults.found_urls}</p>
                          <p className="text-sm text-blue-500">URLs Found</p>
                        </div>
                        <div className="p-4 bg-emerald-500/10 rounded-xl flex-1">
                          <p className="text-2xl font-bold text-emerald-400">{importResults.success?.length || 0}</p>
                          <p className="text-sm text-emerald-500">Imported Successfully</p>
                        </div>
                        <div className="p-4 bg-red-500/10 rounded-xl flex-1">
                          <p className="text-2xl font-bold text-red-400">{importResults.failed?.length || 0}</p>
                          <p className="text-sm text-red-400">Failed to Import</p>
                        </div>
                      </div>

                      {importResults.success && importResults.success.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-emerald-300 mb-2">✅ Successfully Imported:</h4>
                          <div className="space-y-1 max-h-60 overflow-y-auto">
                            {importResults.success.map((item, idx) => (
                              <div key={idx} className="text-sm text-emerald-300 bg-green-50 p-3 rounded flex justify-between items-center">
                                <div>
                                  <p className="font-semibold">{item.title}</p>
                                  <p className="text-xs text-emerald-500 font-mono truncate">{item.url}</p>
                                </div>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => navigate(`/property/${item.id}`)}
                                  className="ml-2"
                                >
                                  View
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {importResults.failed && importResults.failed.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-red-300 mb-2">❌ Failed to Import:</h4>
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {importResults.failed.map((item, idx) => (
                              <div key={idx} className="text-xs text-red-400 bg-red-50 p-2 rounded">
                                <p className="font-mono truncate">{item.url}</p>
                                <p className="text-red-400">{item.error}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button onClick={() => navigate('/admin/dashboard')} className="bg-emerald-600 hover:bg-emerald-500">
                          View All Properties in Dashboard
                        </Button>
                        <Button variant="outline" onClick={() => setImportResults(null)}>
                          Import More
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {importResults.type === 'linevoom' && (
                <div className="space-y-4">
                  <Alert className={importResults.success.length > 0 ? "bg-purple-500/5 border-purple-500/20" : "bg-red-500/5 border-red-500/20"}>
                    <AlertDescription className={importResults.success.length > 0 ? "text-purple-300" : "text-red-300"}>
                      {importResults.success.length > 0 ? (
                        <>
                          <strong>🎉 LINE VOOM Import Complete!</strong><br/>
                          Successfully imported <strong>{importResults.success.length}</strong> of <strong>{importResults.total}</strong> properties from LINE VOOM.
                        </>
                      ) : (
                        <>
                          <strong>❌ Import Failed</strong><br/>
                          Failed to import any properties. Check the error messages below.
                        </>
                      )}
                    </AlertDescription>
                  </Alert>

                  <div className="flex gap-4">
                    <div className="p-4 bg-purple-500/10 rounded-xl flex-1">
                      <p className="text-2xl font-bold text-purple-400">{importResults.success.length}</p>
                      <p className="text-sm text-purple-400">Successfully Imported</p>
                    </div>
                    {importResults.failed.length > 0 && (
                      <div className="p-4 bg-red-500/10 rounded-xl flex-1">
                        <p className="text-2xl font-bold text-red-400">{importResults.failed.length}</p>
                        <p className="text-sm text-red-400">Failed</p>
                      </div>
                    )}
                  </div>

                  {importResults.success.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-purple-300 mb-2">✅ Successfully Imported:</h4>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {importResults.success.map((item, idx) => (
                          <div key={idx} className="bg-[#0A0E27] p-3 rounded-xl border border-purple-500/20 flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-white">{item.title}</p>
                              <p className="text-xs text-[#475569] truncate">{item.url}</p>
                              <p className="text-xs text-purple-400 mt-1">📸 {item.images_count} images imported</p>
                            </div>
                            <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 ml-2" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {importResults.failed.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-red-300 mb-2">❌ Failed Imports:</h4>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {importResults.failed.map((item, idx) => (
                          <div key={idx} className="bg-red-50 p-3 rounded border border-red-500/20">
                            <p className="text-xs text-[#475569] truncate mb-1">{item.url}</p>
                            <p className="text-sm text-red-400">{item.error}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Alert className="bg-blue-500/5 border-blue-500/20">
                    <AlertDescription className="text-blue-300 text-sm">
                      <strong>📸 Next Step:</strong> Add more images to your imported properties!
                      <br/>Go to Dashboard → Click the purple <strong>📸 button</strong> on each property → Paste image URLs from your Line Voom posts
                    </AlertDescription>
                  </Alert>

                  <div className="flex gap-2">
                    <Button onClick={() => navigate('/admin/dashboard')} className="bg-purple-600 hover:bg-purple-500">
                      View All Properties in Dashboard
                    </Button>
                    <Button variant="outline" onClick={() => setImportResults(null)}>
                      Import More
                    </Button>
                  </div>
                </div>
              )}

              {importResults.type === 'csv' && (
                <div className="space-y-4">
                  <Alert className="bg-emerald-500/5 border-emerald-500/20">
                    <AlertDescription className="text-emerald-300">
                      <strong>🎉 CSV Import Complete!</strong><br/>
                      Successfully imported <strong>{importResults.imported_count}</strong> properties from CSV file.
                    </AlertDescription>
                  </Alert>

                  <div className="flex gap-4">
                    <div className="p-4 bg-emerald-500/10 rounded-xl flex-1">
                      <p className="text-2xl font-bold text-emerald-400">{importResults.imported_count}</p>
                      <p className="text-sm text-emerald-500">Properties Imported</p>
                    </div>
                    {importResults.skipped_count > 0 && (
                      <div className="p-4 bg-yellow-500/10 rounded-xl flex-1">
                        <p className="text-2xl font-bold text-yellow-300">{importResults.skipped_count}</p>
                        <p className="text-sm text-yellow-600">Skipped (Duplicates)</p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={() => navigate('/admin/dashboard')} className="bg-emerald-600 hover:bg-emerald-500">
                      View All Properties in Dashboard
                    </Button>
                    <Button variant="outline" onClick={() => setImportResults(null)}>
                      Import More
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tips */}
        <Card className="mt-8 border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-white">Import Tips & Troubleshooting</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-[#D4AF37] mb-2">✅ What Works:</h4>
                <ul className="space-y-1 text-sm text-[#94A3B8]">
                  <li>✓ System automatically extracts: title, description, price, bedrooms, bathrooms, size, images</li>
                  <li>✓ Property type (House/Condo/Commercial/Retail) detected from content</li>
                  <li>✓ Listing type (Sale/Rental) detected from URL and content</li>
                  <li>✓ Can import from multiple sites simultaneously in bulk mode</li>
                  <li>✓ After import, you can edit any details in the Admin Dashboard</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-red-300 mb-2">⚠️ If Import Fails (403 Forbidden Error):</h4>
                <ul className="space-y-1 text-sm text-[#94A3B8]">
                  <li>1. <strong>Try a different property URL</strong> from the same website</li>
                  <li>2. <strong>Use Listing Crawler instead</strong> - it often works better than individual imports</li>
                  <li>3. <strong>Try a different real estate site</strong> (thailand-property.com, fazwaz.com work well)</li>
                  <li>4. <strong>Wait 30 seconds and retry</strong> - some sites limit request rates</li>
                  <li>5. <strong>Manual entry</strong> - Go to Dashboard → "Add New Property" and enter details manually</li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-300 mb-2">💡 Pro Tip:</h4>
                <p className="text-sm text-blue-300">
                  If a specific website keeps blocking imports, <strong>use the Listing Crawler</strong> on their search results page instead. 
                  It's often more successful because it mimics normal browsing behavior!
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-emerald-300 mb-2">🎯 Best Success Rate:</h4>
                <ul className="space-y-1 text-sm text-[#94A3B8]">
                  <li>✓ <strong>thailand-property.com</strong> - Usually works great</li>
                  <li>✓ <strong>fazwaz.com</strong> - Good compatibility</li>
                  <li>✓ <strong>Listing Crawler</strong> - Best for bulk imports</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PropertyImport;
